/**
 * `@cova/settings-core/paths`: the ONE section-path table (spec multi-product
 * 021 G47 (7), 023 G60 (3)). The client's `settingsLink(key)` and the server's
 * `settingsUrl(key, recipient)` both read `sectionPath(app, key)`, so a
 * section's path is written once per app (R-021-5: no other section-path map).
 *
 * Paths are firm-relative. An in-app link adds whatever its app puts in front;
 * a server link (email, notification) uses `firmSectionPath`, which puts the
 * firm's home on that app in front (`/o/<slug>/` on Bots) so the link opens in
 * the recipient's right firm. The firm's home is the app's own routing fact
 * (`APP_HOMES[app].homePath` in `@cova/shell/home`), not this table's: the
 * platform's is `/` until it moves to `/o/<slug>/` (007), and its settings
 * links follow it with no edit here.
 *
 * Platform paths are today's `/settings/...` routes (OQ-021-2). The personal
 * sections mount at `/settings/profile`, `/settings/security` (sign-in and
 * security, the platform's existing page), `/settings/sessions` and
 * `/settings/firms` (WP3.16.3d), and Notifications and Sign out at
 * `/settings/notifications` and `/settings/sign-out`, inside the settings
 * frame that mounts the host (A21-2/3). Seats has its own page, `/settings/seats`
 * (WP3.16.3b), beside People at `/settings/members`, and Usage its own,
 * `/settings/usage` (WP3.16.3c2). Paths for a section with no page yet
 * (platform `forms-branding`; most Bots settings) are the route the unit
 * building it mounts; that unit may move one here, and only here. Bots'
 * personal sections all sit in its settings window, `/settings/<key>` (N2);
 * its `/account` page stays Clerk's own profile for the account menu.
 */
import { SECTION_PATHS, sectionPath } from "@cova/agent-v2-core/contracts/agent-products"

import type { CovaApp, SettingsScope } from "../vocabulary"
import type { SectionKeyFor } from "../registry/section-keys"

/**
 * The table and its reader live in `@cova/agent-v2-core/contracts/agent-products`
 * (D56), beside `ADMIN_SECTIONS`, so the agent's route metadata links through
 * it too; re-exported here so `@cova/settings-core/paths` is unchanged.
 */
export { SECTION_PATHS, sectionPath }

/**
 * A section's path inside one firm, for links rendered outside any app (G60 (3)):
 * the firm's home path on `app` (`APP_HOMES[app].homePath(segment)`, e.g.
 * `/o/acme/` on Bots) followed by the section's path. `settingsUrl` on the
 * server is its one caller; an in-app link uses `sectionPath` alone.
 */
export const firmSectionPath = <A extends CovaApp>(app: A, key: SectionKeyFor<A>, firmHomePath: string): string =>
  `${firmHomePath.replace(/\/+$/, "")}${sectionPath(app, key)}`

/**
 * The firm types the platform app hosts (staff is the platform's own firm;
 * business firms live on Bots, so their links go through `sectionPath("bots", key)`).
 */
export type PlatformHostedScope = Exclude<SettingsScope, "business">

/** Which firm types the platform app hosts, total over `SettingsScope` so a new scope must answer. */
const PLATFORM_HOSTS: { readonly [S in SettingsScope]: S extends PlatformHostedScope ? true : false } = {
  "insurance-broker": true,
  underwriter: true,
  business: false,
  staff: true,
}

/** Whether the platform app hosts firms of `scope` (a business firm's links go through Bots). */
export const isPlatformHostedScope = (scope: SettingsScope): scope is PlatformHostedScope => PLATFORM_HOSTS[scope]

/**
 * Scopes whose firms the platform app serves under a tree of their own, with
 * their own settings pages (the underwriter app at `/underwriter`, spec
 * underwriter 2026-10-17-001 D41). A row lists the sections that scope has a
 * page for; a key it does not list has no page there. A scope with no row
 * uses the platform's own `sectionPath` row. Part of the ONE table: R-021-5
 * allows no other section-path map, and this row retires when the underwriter
 * tree joins the platform's settings frame (021 S5).
 */
const UNDERWRITER_SECTION_PATHS = {
  general: "/underwriter/settings/organisation",
  people: "/underwriter/settings/team",
  seats: "/underwriter/settings/team",
  billing: "/underwriter/settings/billing",
  profile: "/underwriter/settings",
  inboxes: "/underwriter/settings/inboxes",
} as const satisfies { readonly [K in SectionKeyFor<"platform">]?: string }

/*
 * The Cova Staff firm's row (R0-6: staff is a shell inside the platform, so
 * its row sits here beside the underwriter tree's, not in the app table). A
 * firm-addressed link to its People or Security lands where the staff shell
 * mounts them (`/cs/settings/...`, `@cova/staff-shell/settings`, 022 G49 (8)),
 * not on the broker pages, and its mailboxes have their page in the same
 * rail. Its other shared and personal sections are the platform's own pages.
 * A staff firm is never billed: its Seats, Billing and PaymentWall paths are
 * the platform's only so the shared keys stay total; the billed-only sections
 * never show for it. The platform's own sections (calls, integrations, ...)
 * have no page for the staff firm here.
 */
const STAFF_SECTION_PATHS = {
  general: sectionPath("platform", "general"),
  people: "/cs/settings/people",
  seats: sectionPath("platform", "seats"),
  billing: sectionPath("platform", "billing"),
  usage: sectionPath("platform", "usage"),
  branding: sectionPath("platform", "branding"),
  security: "/cs/settings/security",
  "connected-apps": sectionPath("platform", "connected-apps"),
  "payment-wall": sectionPath("platform", "payment-wall"),
  profile: sectionPath("platform", "profile"),
  "sign-in-security": sectionPath("platform", "sign-in-security"),
  sessions: sectionPath("platform", "sessions"),
  notifications: sectionPath("platform", "notifications"),
  firms: sectionPath("platform", "firms"),
  "sign-out": sectionPath("platform", "sign-out"),
  inboxes: "/cs/settings/inboxes",
} as const satisfies { readonly [K in SectionKeyFor<"platform">]?: string }

/** Where the staff shell's settings rail opens (`/cs/settings`): the staff firm's settings home. */
const STAFF_SETTINGS_HOME = "/cs/settings"

export const SCOPE_SECTION_PATHS: {
  readonly [S in PlatformHostedScope]?: { readonly [K in SectionKeyFor<"platform">]?: string }
} = {
  underwriter: UNDERWRITER_SECTION_PATHS,
  staff: STAFF_SECTION_PATHS,
}

/**
 * Where a firm of `scope` keeps its settings on the platform host: `settings`
 * is the page a person lands on when they reach for settings without naming a
 * section (a holder of another scope sent off core `/settings`, a plan gate's
 * member link, `<settings>/billing`), and `inboxes` is where a completed
 * mailbox connect lands. The scope registry's `settingsPath` and
 * `inboxSettingsPath` read these (R-021-5, 021 S5: the registry is not a
 * second section-path map). Every value is a row of this module's table.
 */
export const SCOPE_SETTINGS_HOMES: {
  readonly [S in PlatformHostedScope]: { readonly settings: string; readonly inboxes: string }
} = {
  "insurance-broker": { settings: sectionPath("platform", "general"), inboxes: sectionPath("platform", "inboxes") },
  underwriter: { settings: UNDERWRITER_SECTION_PATHS.profile, inboxes: UNDERWRITER_SECTION_PATHS.inboxes },
  staff: { settings: STAFF_SETTINGS_HOME, inboxes: STAFF_SECTION_PATHS.inboxes },
}

/**
 * The platform path of a section for a firm of `scope` (a server link that
 * knows the firm's scope but not a person, such as a billing email to the
 * firm's billing address). `undefined` when the scope has no page for `key`.
 */
export const scopeSectionPath = (scope: PlatformHostedScope, key: SectionKeyFor<"platform">): string | undefined => {
  const row = SCOPE_SECTION_PATHS[scope]
  return row === undefined ? sectionPath("platform", key) : row[key]
}
